import { Card, CardHeader, Divider, Box, Button, IconButton, Tooltip, Typography } from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { ListContextProvider, ShowBase, useDeleteWithConfirmController, useListContext } from "ra-core";
import { Dispatch, FC, SetStateAction, useRef, useState, useEffect } from "react";
import { Location } from "history"
import { Link as RouteLink } from "react-router-dom"
import { Datagrid } from "@/components/list/datagrid";
import { Pagination as DefaultPagination } from '@/components/list/pagination'
import Scrollbar from "@/components/Scrollbar";
import { RecPatch } from "@/dataProvider/tools";
import TextField from "@/components/field/TextField";
import useLocalListController from "@/components/list/useLocalListController";
import { useResourceRegister } from "@/utils/resourceRegister";
import EditRole from "@/components/settings/users/EditRole";
import Edit from "@/controller/Edit";
import Create from "@/controller/Create";
import { useDefaultSavedHandlers } from "@/controller/defaultSavedHandlers";
import { RoleResponseDto } from "@/gen/api/user";
import { FunctionField } from "@/components/field";
import Confirm from "@/components/widgets/Confirm";
import ViewDefaultRole from "@/components/settings/users/ViewDefaultRole";


const listOptions = {
  sorters: {
    id: (a:any, b:any) => b.id - a.id,
    name: (a:RoleResponseDto, b:RoleResponseDto) => (a.name ?? '') < (b.name ?? '') ? -1 : 1,
  }
}
const showOnlyCustom = (role: RoleResponseDto) => !role.isDefault
const showAll = (role: RoleResponseDto) => true

const RolesList: FC = () => {
  useResourceRegister({ name: 'org-roles-permits' })
  useResourceRegister({ name: 'org-roles-default' })
  
  const [states, setStates] = useState({
    showOnlyCustom: true,
  })
  const listCtrl = useLocalListController({ resource: 'org-roles', basePath: '', filter: { custom: showOnlyCustom } }, listOptions as any)
  const setFiltersRef = useRef(listCtrl.setFilters)
  setFiltersRef.current = listCtrl.setFilters

  useEffect(() => {
    if (states.showOnlyCustom) {
      setFiltersRef.current({ custom: showOnlyCustom }, {})
    } else {
      setFiltersRef.current({ custom: showAll }, {})
    }
  }, [states.showOnlyCustom])
  return (
    <ListContextProvider value={listCtrl}>
      <RolesListBody showOnlyCustom={states.showOnlyCustom} setUpperState={setStates} />
    </ListContextProvider>
  )
}

const RolesListBody: FC<{
  showOnlyCustom: boolean,
  setUpperState: Dispatch<SetStateAction<{
    showOnlyCustom: boolean;
  }>>
}> = ({showOnlyCustom, setUpperState}) => {
  const listCtx = useListContext<RoleResponseDto & RecPatch>()
  const { refetch } = listCtx

  const [states, setStates] = useState({
    openAdd: false,
    toEdit: null as RoleResponseDto|null,
    toDelete: null as RoleResponseDto|null,
    toView: null as RoleResponseDto|null,
  })
  const createHandler = useDefaultSavedHandlers("Create Custom Role")
  createHandler.onSuccess.merge(refetch)
  const updateHandler = useDefaultSavedHandlers("Update Custom Role")
  updateHandler.onSuccess.merge(refetch)

  const delHandlers = useDefaultSavedHandlers('Delete Custom Role')
  delHandlers.onSuccess.merge(refetch)
  const {
    open: openConfirm,
    loading: deleting,
    handleDelete,
    handleDialogClose,
    handleDialogOpen,
  } = useDeleteWithConfirmController({ 
    resource: 'org-roles',
    record: states.toDelete as any,
    ...delHandlers,
  })
  return (
    <>
      <Button variant="text" size="small" component={RouteLink}
        to={(location: Location) => ({...location, search: undefined })}
        startIcon={<ArrowBackIcon />}
        sx={{ mt: -1.5, mb: 1.5 }}
      >
        Back to User List
      </Button>
      <Card>
        <CardHeader title="Roles" 
          action={
            <>
              <Button size="small" variant="text"
                onClick={() => setUpperState(v => ({...v, showOnlyCustom: !v.showOnlyCustom}))}
              >
                {showOnlyCustom ? "Show Built-In Roles" : "Show only Custom Roles"}
              </Button>
              <Button color="primary" size="small" variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setStates(v => ({...v, openAdd: true }))}
                sx={{ ml: 2 }}
              >
                New Custom Role
              </Button>
            </>
          }
        />
        <Divider />
        <Scrollbar options={{ suppressScrollY: true }}>
          <Box sx={{ minWidth: 1150 }}>
            <Datagrid 
              hasBulkActions={false} 
              sx={{ 
                '& th' : { py: 2 },
                '& td' : { py: 2 },
                '& .value': { maxWidth: 350, display: 'inline-block', wordBreak: 'break-word' },
                '& .description': { maxWidth: 550, display: 'inline-block', wordBreak: 'break-word' },
              }}
              {...listCtx}
            >
              <TextField source="name" label="Display Name" sortable={true} />
              <TextField source="value" label="Value" sortable={false} className="value" />
              <TextField source="description" label="Description" sortable={false} className="description" />
              {!showOnlyCustom &&
                <FunctionField label="Role Type" sortable={false} 
                  render={(d?:RoleResponseDto&RecPatch) => {
                    if (!d) { return null }
                    return (d.isDefault ? "Built In" : "Custom Role")
                  }}
                />}
              <FunctionField source="action" label="Action" sortable={false} 
                render={(d?:RoleResponseDto&RecPatch) => {
                  if (!d) { return null }
                  if (!showOnlyCustom && d.isDefault) { return (
                    <Tooltip title="View">
                      <IconButton
                        sx={{
                          color: 'text.primary' as any,
                          '&:hover': { backgroundColor: 'action.hover' },
                        }}
                        onClick={() => {
                          setStates(v => ({...v,
                            toView: d,
                          }))
                        }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) }
                  return (
                    <>
                      <Tooltip title="Edit">
                        <IconButton
                          sx={{
                            color: 'text.primary' as any,
                            '&:hover': { backgroundColor: 'action.hover' },
                          }}
                          onClick={() => {
                            setStates(v => ({...v,
                              toEdit: d,
                            }))
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          sx={{
                            color: 'text.primary' as any,
                            '&:hover': { backgroundColor: 'action.hover' },
                          }}
                          onClick={(e:any) => {
                            setStates(v => ({...v, toDelete: d }))
                            handleDialogOpen(e)
                          }}
                        >
                          <DeleteForeverIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )
                }}
              />
            </Datagrid>
          </Box>
        </Scrollbar>
        {//@ts-ignore 
        <DefaultPagination {...listCtx} />}
      </Card>
      <Create resource="org-roles" basePath="" {...createHandler}>
        <EditRole open={states.openAdd} onClose={() => setStates(v => ({...v, openAdd: false }))} />
      </Create>
      <Edit resource={states.toEdit ? "org-roles" : 'noop'} basePath="" id={states.toEdit ? (states.toEdit as any).id : null} {...updateHandler} mutationMode="pessimistic">
        <EditRole open={!!states.toEdit} onClose={() => setStates(v => ({...v, toEdit: null }))} />
      </Edit>
      <ShowBase resource={states.toView ? "org-roles-default" : 'noop'} basePath="" id={states.toView?.roleId}>
        <ViewDefaultRole open={!!states.toView} onClose={() => setStates(v => ({...v, toView: null }))} />
      </ShowBase>
      <Confirm
        isOpen={openConfirm}
        title={"Delete Custom Role"}
        onConfirm={handleDelete}
        onClose={(e:any) => {handleDialogClose(e); setStates(v => ({...v, toDelete: null })) }}
        loading={deleting}
        confirm="Confirm"
        cancel="Cancel"
        content={(
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 550 }}>
              <Box>
                <Typography
                  variant="body2"
                  sx={{ mt: 2 }}
                >
                  Please confirm whether to proceed the deletion of this
                  {` "${states.toDelete?.name || ''}" `}
                  custom role.
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      />
    </>
  )
}

export default RolesList;
